import { ApolloCache, InMemoryCache } from '@apollo/client';
import { Button, Divider, Flex, Progress, Stack, Text, TextArea, Truncate } from '@eleveo/components-react';
import { ChatIcon, CheckmarkIcon, CrossIcon, SwitchPersonIcon } from '@eleveo/icons-react';
import clsx from 'clsx';
import { pathOr } from 'ramda';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../../../app/AuthorizationProvider';
import { RequestStatusComponent } from '../../../components/AgentsRequests';
import { DateFormatter } from '../../../components/DateFormatter';
import { Center } from '../../../components/LayoutNext/components/Center';
import { AgentShiftTile } from '../../../components/MySchedule/AgentShiftTile';
import { REQUEST_STATE_PROPS } from '../../../config/agentsRequests';
import {
  GetRecipientShiftSwapRequestDocument,
  RequestState,
  RequestSubtype,
  ShiftSwapRequestAction,
  ShiftSwapRequestController,
  ScheduleElement,
  ShiftSwapRequestState,
  useGetRecipientShiftSwapRequestQuery,
  useGetRequesterShiftSwapRequestQuery,
  useUpdateStateOfShiftSwapRequestMutation,
} from '../../../graphql/components';
import { formatNameOfPerson } from '../../../utils/helpers';
import { max255chars } from '../../../utils/validations';
import { RequestTileProps } from './RequestPTOTile';

type Props = RequestTileProps & {
  scheduleElements: ScheduleElement[];
};

export const RequestShiftSwapTile = ({
  isExpanded,
  onCancel,
  request,
  scheduleElements,
  updateStateOfRequest,
}: Props) => {
  const { t } = useTranslation();
  const { userId: agentId, timezone } = useAuth();

  const [messageForRequester, setMessageForRequester] = useState<string | undefined>(undefined);

  const scrollToViewRef = useCallback((elRef: HTMLDivElement) => {
    /**
     * TODO: investigate if this could be handled more properly...
     * see https://fosstodon.org/@tahazsh/112128065110906980
     */
    // wait for opening animation to be executed
    setTimeout(() => {
      // ref is only needed to scroll into view, this block will be executed only when accordion item is expanded and data got loaded successfully
      elRef?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    }, 200); // TODO: 200ms is the current transition time set on UI kit for opening/closing animation, which will need to be changed in near future
  }, []);

  const messageForRequesterErrorMessage = useMemo(() => {
    const errorMessage = max255chars(messageForRequester);

    if (!errorMessage) {
      return undefined;
    }

    return t(errorMessage, { characters: 255 });
  }, [messageForRequester, t]);

  const {
    data: recipientShiftSwapData,
    loading: recipientShiftSwapLoading,
    error: recipientShiftSwapError,
    refetch: refetchRecipientShiftSwapRequest,
  } = useGetRecipientShiftSwapRequestQuery({
    variables: {
      recipientId: agentId,
      shiftSwapRequestId: request.id,
    },
    fetchPolicy: 'network-only',
    skip: !isExpanded || request.subtype === RequestSubtype.Requester,
    notifyOnNetworkStatusChange: true, // Needed to run `onCompleted` after `refetch()`
    onCompleted: (data) => {
      if ((data.recipientShiftSwapRequest.state as unknown as RequestState) !== request.state) {
        updateStateOfRequest(request, data.recipientShiftSwapRequest.state as unknown as RequestState);
      }
    },
  });

  const {
    data: requesterShiftSwapData,
    loading: requesterShiftSwapLoading,
    error: requesterShiftSwapError,
  } = useGetRequesterShiftSwapRequestQuery({
    variables: {
      requesterId: agentId,
      shiftSwapRequestId: request.id,
    },
    fetchPolicy: 'network-only',
    skip: !isExpanded || request.subtype === RequestSubtype.Recipient,
    notifyOnNetworkStatusChange: true, // Needed to run `onCompleted` after `refetch()`
    onCompleted: (data) => {
      if ((data.requesterShiftSwapRequest.state as unknown as RequestState) !== request.state) {
        updateStateOfRequest(request, data.requesterShiftSwapRequest.state as unknown as RequestState);
      }
    },
  });

  const [updateStateOfShiftSwapRequest, { loading: isUpdateStateOfShiftSwapRequestInProgress }] =
    useUpdateStateOfShiftSwapRequestMutation();

  const isLoading = recipientShiftSwapLoading || requesterShiftSwapLoading;
  const isError = recipientShiftSwapError || requesterShiftSwapError;
  const isMutationInProgress = isUpdateStateOfShiftSwapRequestInProgress;

  const shiftSwapRequest =
    recipientShiftSwapData?.recipientShiftSwapRequest ?? requesterShiftSwapData?.requesterShiftSwapRequest;

  const isPendingApprovalState = shiftSwapRequest?.state === ShiftSwapRequestState.PendingApproval;
  const isApprovedState = shiftSwapRequest?.state === ShiftSwapRequestState.Approved;

  const isAcceptedOrApprovedState = isPendingApprovalState || isApprovedState;

  const isDeclinedState = shiftSwapRequest?.state === ShiftSwapRequestState.Declined;
  const isCanceledState = shiftSwapRequest?.state === ShiftSwapRequestState.Canceled;

  const isDeclinedOrCanceledState = isDeclinedState || isCanceledState;

  const handleStateChangeOfRecipientShiftSwapRequest = async (action: ShiftSwapRequestAction) => {
    if (messageForRequesterErrorMessage || !shiftSwapRequest) {
      return;
    }

    try {
      await updateStateOfShiftSwapRequest({
        variables: {
          agentId,
          requestId: shiftSwapRequest.id,
          action,
          controller: ShiftSwapRequestController.Recipients,
          parameters: {
            fromState: shiftSwapRequest.state as unknown as RequestState,
            message: messageForRequester,
          },
        },
        update: (cache: ApolloCache<InMemoryCache>, result) => {
          const updatedShiftSwapRequest = result.data?.updateStateOfShiftSwapRequest;

          if (!updatedShiftSwapRequest) {
            return Promise.reject();
          }

          cache.writeQuery({
            query: GetRecipientShiftSwapRequestDocument,
            variables: {
              recipientId: agentId,
              shiftSwapRequestId: shiftSwapRequest.id,
            },
            data: {
              recipientShiftSwapRequest: updatedShiftSwapRequest,
            },
          });

          updateStateOfRequest(request, updatedShiftSwapRequest.state as unknown as RequestState);

          return Promise.resolve();
        },
      });

      // clean-up message for requester
      setMessageForRequester(undefined);
    } catch (err) {
      // `customFetch()` will display error message

      const errorCode = pathOr(undefined, ['networkError', 'result', 'errorCode'], err);

      // there could be various state inconsistencies, however the message should be purged for all of them
      if (errorCode?.match(/fe.sch.pto.request.state.transition..*.unsupported/)) {
        setMessageForRequester(undefined);
      }

      // update detail view of Shift swap request in case of data manipulation between action
      await refetchRecipientShiftSwapRequest();
    }
  };

  const [firstAudit, ...restOfAudits] = shiftSwapRequest?.audits ?? [];

  return (
    <>
      {isLoading && (
        <Center>
          <Progress className="mx-auto py-40" />
        </Center>
      )}

      {isMutationInProgress && <Progress withOverlay />}

      {!isLoading && !isError && shiftSwapRequest && (
        <>
          <div className="px-20 pt-10 pb-15" ref={scrollToViewRef}>
            <Flex className="w-full" column>
              <Flex alignItems="baseline" justifyContent="between" className="w-full mb-10" gap={[10, 0]} wrap>
                <Flex alignItems="center" className="max-w-full" wrap gap={[10, 0]}>
                  <span className="text-sm">{t('form.status')}:</span>
                  <RequestStatusComponent
                    status={shiftSwapRequest?.state as unknown as RequestState}
                    testId="agent-portal:monthly-view:requests-sidebar:shift-swap-request-status"
                  />
                </Flex>
                <Flex
                  justifyContent="end"
                  className="text-right text-sm flex-1 wrapper__tiny-column-gap"
                  testId="agent-portal:monthly-view:requests-sidebar:shift-swap-request-createdAt-timestamp"
                  wrap
                >
                  {t('form.created')}:
                  <DateFormatter
                    className="font-bold"
                    options={{ dateStyle: 'short', timeStyle: 'short', timeZone: timezone }}
                  >
                    {new Date(shiftSwapRequest?.createdAt)}
                  </DateFormatter>
                </Flex>
              </Flex>
            </Flex>

            {request.subtype === RequestSubtype.Requester && (
              <>
                {firstAudit.message && (
                  <>
                    <Divider className="mt-10" />
                    <Stack
                      className="w-full"
                      direction="vertical"
                      spacing={0}
                      testId="agent-portal:monthly-view:requests-sidebar:shift-swap-request-audit-log"
                    >
                      <Flex
                        className="mt-10"
                        testId="agent-portal:monthly-view:requests-sidebar:shift-swap-request-audit-log:message"
                      >
                        <ChatIcon className="text-blue-700 text-lg" />
                        <span className="ml-10 break-words overflow-auto">{firstAudit.message}</span>
                      </Flex>
                    </Stack>
                    <Divider className="mt-10" />
                  </>
                )}

                <span className="mr-auto text-xs py-10">{t('schedule.request.offeredShift')}:</span>

                <AgentShiftTile
                  className="shift-swap-request__agent-shift-tile"
                  {...shiftSwapRequest.requesterShift}
                  isSelected={false}
                  scheduleElements={scheduleElements}
                  shiftSubtype={RequestSubtype.Requester}
                  shiftSwapRequestId={request.id}
                  testIdSlug="agent-portal:monthly-view:requests-sidebar:shift-swap-request:requester"
                />

                <Stack className="w-full pt-10 -mb-15">
                  <Divider className="flex-1 my-auto" />
                  {isAcceptedOrApprovedState ? (
                    <CheckmarkIcon className="text-2xl" style={{ color: 'green' }} />
                  ) : (
                    <SwitchPersonIcon className={clsx('text-2xl', { 'text-grey-500': isDeclinedOrCanceledState })} />
                  )}
                  <Divider className="flex-1 my-auto" />
                </Stack>

                <span className="mr-auto text-xs pb-10">{t('schedule.request.requiredShift')}:</span>

                <AgentShiftTile
                  className={clsx('shift-swap-request__agent-shift-tile', {
                    'mb-15': restOfAudits.length,
                    'mb-20': !restOfAudits.length,
                    'shift-swap-request__agent-shift-tile--accepted': isAcceptedOrApprovedState,
                  })}
                  {...shiftSwapRequest.recipientShift}
                  isDisabled={isDeclinedOrCanceledState}
                  isSelected={false}
                  scheduleElements={scheduleElements}
                  shiftSubtype={RequestSubtype.Recipient}
                  shiftSwapRequestId={request.id}
                  testIdSlug="agent-portal:monthly-view:requests-sidebar:shift-swap-request:recipient"
                />

                {/* Only Pending (approval/acceptance) requests can be canceled by requesters! */}
                {[ShiftSwapRequestState.PendingAcceptance, ShiftSwapRequestState.PendingApproval].includes(
                  shiftSwapRequest.state
                ) &&
                  onCancel && (
                    <Flex justifyContent="end">
                      <Button
                        color="warning"
                        isSmall
                        onClick={onCancel}
                        variant="outline"
                        testId="agent-portal:monthly-view:requests-sidebar:shift-swap-request-audit-log:cancel-button"
                      >
                        <CrossIcon /> {t('schedule.request.cancelRequest')}
                      </Button>
                    </Flex>
                  )}
              </>
            )}

            {request.subtype === RequestSubtype.Recipient && (
              <>
                <span className="mr-auto text-xs py-10">{t('schedule.request.offeredShift')}:</span>

                <AgentShiftTile
                  className={clsx('shift-swap-request__agent-shift-tile', {
                    'shift-swap-request__agent-shift-tile--accepted': isAcceptedOrApprovedState,
                  })}
                  {...shiftSwapRequest.requesterShift}
                  isSelected={false}
                  isDisabled={isDeclinedOrCanceledState}
                  scheduleElements={scheduleElements}
                  shiftSubtype={RequestSubtype.Requester}
                  shiftSwapRequestId={request.id}
                  testIdSlug="agent-portal:monthly-view:requests-sidebar:shift-swap-request:requester"
                />

                {firstAudit.message && (
                  <Stack
                    className="w-full"
                    direction="vertical"
                    spacing={0}
                    testId="agent-portal:monthly-view:requests-sidebar:shift-swap-request-audit-log"
                  >
                    <Flex
                      className="mt-10"
                      testId="agent-portal:monthly-view:requests-sidebar:shift-swap-request-audit-log:message"
                    >
                      <ChatIcon className="text-blue-700 text-lg" />
                      <span className="ml-10 break-words overflow-auto">{firstAudit.message}</span>
                    </Flex>
                  </Stack>
                )}

                <Stack className="w-full pt-10 -mb-15">
                  <Divider className="flex-1 my-auto" />
                  {isAcceptedOrApprovedState ? (
                    <CheckmarkIcon className="text-2xl" style={{ color: 'green' }} />
                  ) : (
                    <SwitchPersonIcon className={clsx('text-2xl', { 'text-grey-500': isDeclinedOrCanceledState })} />
                  )}
                  <Divider className="flex-1 my-auto" />
                </Stack>

                <span className="mr-auto text-xs pb-10">{t('schedule.request.requiredShift')}:</span>

                <AgentShiftTile
                  className="shift-swap-request__agent-shift-tile"
                  {...shiftSwapRequest.recipientShift}
                  isSelected={false}
                  scheduleElements={scheduleElements}
                  shiftSubtype={RequestSubtype.Recipient}
                  shiftSwapRequestId={request.id}
                  testIdSlug="agent-portal:monthly-view:requests-sidebar:shift-swap-request:recipient"
                />

                {request.state === RequestState.PendingAcceptance && (
                  <>
                    <TextArea
                      className="w-full py-15"
                      errorMessage={messageForRequesterErrorMessage}
                      label={t('schedule.request.message')}
                      onChange={(message) => setMessageForRequester(message.length ? message : undefined)}
                      testId="requests-sidebar:recipient:message"
                      validationState={messageForRequesterErrorMessage ? 'invalid' : undefined}
                      value={messageForRequester ?? ''}
                    />

                    <Flex justifyContent="between">
                      <Button
                        color="error"
                        isSmall
                        onClick={() => handleStateChangeOfRecipientShiftSwapRequest(ShiftSwapRequestAction.Decline)}
                        testId="agent-portal:monthly-view:requests-sidebar:shift-swap-request:recipient:decline-button"
                      >
                        <CrossIcon /> {t('layout.action.decline')}
                      </Button>

                      <Button
                        color="success"
                        isSmall
                        onClick={() => handleStateChangeOfRecipientShiftSwapRequest(ShiftSwapRequestAction.Accept)}
                        testId="agent-portal:monthly-view:requests-sidebar:shift-swap-request:recipient:accept-button"
                      >
                        <CheckmarkIcon /> {t('layout.action.accept')}
                      </Button>
                    </Flex>
                  </>
                )}

                {/* Only Pending approval requests can be canceled by recipient! */}
                {isPendingApprovalState && onCancel && (
                  <Flex justifyContent="end" className="my-15">
                    <Button
                      color="warning"
                      isSmall
                      onClick={onCancel}
                      variant="outline"
                      testId="agent-portal:monthly-view:requests-sidebar:shift-swap-request-audit-log:cancel-button"
                    >
                      <CrossIcon /> {t('schedule.request.cancelAcceptance')}
                    </Button>
                  </Flex>
                )}
              </>
            )}

            <div
              className={clsx({ 'pb-10': restOfAudits.length })}
              data-test-id="agent-portal:monthly-view:requests-sidebar:shift-swap-request-audit-logs-section"
            >
              {restOfAudits.map(({ toState, when, who, message: auditMessage }) => (
                <React.Fragment key={`${toState}-${when}`}>
                  <Divider className="mt-10" />
                  <Stack
                    className="w-full"
                    direction="vertical"
                    spacing={0}
                    testId="agent-portal:monthly-view:requests-sidebar:shift-swap-request-audit-log"
                  >
                    <Stack className="mt-10 text-sm">
                      <Truncate>
                        <Text
                          weight="bold"
                          data-test-id="agent-portal:monthly-view:requests-sidebar:shift-swap-request-audit-log:to-state"
                        >
                          {t(REQUEST_STATE_PROPS[toState].nameKey)}:{' '}
                        </Text>
                        <Text
                          weight="bold"
                          data-test-id="agent-portal:monthly-view:requests-sidebar:shift-swap-request-audit-log:person"
                        >
                          {formatNameOfPerson(who.firstName, who.lastName)}
                        </Text>
                      </Truncate>
                      <DateFormatter
                        className="flex-none font-bold ml-auto"
                        options={{ dateStyle: 'short', timeStyle: 'short', timeZone: timezone }}
                      >
                        {new Date(when)}
                      </DateFormatter>
                    </Stack>
                    {auditMessage && (
                      <Flex
                        className="mt-10"
                        testId="agent-portal:monthly-view:requests-sidebar:shift-swap-request-audit-log:message"
                      >
                        <ChatIcon className="text-blue-700 text-lg" />
                        <span className="ml-10 break-words overflow-auto">{auditMessage}</span>
                      </Flex>
                    )}
                  </Stack>
                </React.Fragment>
              ))}
            </div>
          </div>
        </>
      )}
    </>
  );
};
